import { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Search, Plus, X } from 'lucide-react';

import { api, qs } from '../api/client.js';
import { InvestorList } from '../components/InvestorList.jsx';
import { InvestorDetail } from '../components/InvestorDetail.jsx';
import { NewInvestorModal } from '../components/NewInvestorModal.jsx';
import './PipelineView.css';

const STATUS_FILTERS = ['Hot', 'Warm', 'Cold', 'New Lead', 'Contacted', 'In Discussion'];
const FIT_FILTERS    = ['High', 'Medium', 'Low'];

export function PipelineView() {
    const queryClient = useQueryClient();
    const [searchTerm, setSearchTerm]   = useState('');
    const [statusFilter, setStatusFilter] = useState(null);
    const [fitFilter, setFitFilter]     = useState(null);
    const [selectedId, setSelectedId]   = useState(null);
    const [creating, setCreating]       = useState(false);

    // Build query params (used as the React Query key)
    const params = useMemo(() => ({
        q: searchTerm || undefined,
        status: statusFilter || undefined,
        fit_level: fitFilter || undefined,
        limit: 200,
        sort: 'name',
        order: 'asc',
    }), [searchTerm, statusFilter, fitFilter]);

    const { data, isLoading, error } = useQuery({
        queryKey: ['investors', params],
        queryFn: () => api.get(`/investors${qs(params)}`),
    });

    const investors = data?.results || [];
    const total = data?.total ?? 0;

    // Auto-select first item if nothing selected
    const effectiveSelectedId = selectedId ?? investors[0]?.id ?? null;

    const clearFilters = () => {
        setSearchTerm('');
        setStatusFilter(null);
        setFitFilter(null);
    };

    const hasFilters = searchTerm || statusFilter || fitFilter;

    return (
        <div className="pipeline">
            {/* Topbar */}
            <header className="pipeline-topbar">
                <div className="pipeline-title-block">
                    <h1 className="display pipeline-title">Pipeline</h1>
                    <span className="pipeline-count mono">
                        {isLoading ? '—' : `${total} investor${total === 1 ? '' : 's'}`}
                    </span>
                </div>

                <div className="pipeline-toolbar">
                    <div className="search">
                        <Search size={15} strokeWidth={1.75} />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search by name, contact, email, notes..."
                        />
                        {searchTerm && (
                            <button onClick={() => setSearchTerm('')} className="search-clear">
                                <X size={14} />
                            </button>
                        )}
                    </div>

                    <button className="btn-secondary" onClick={() => setCreating(true)}>
                        <Plus size={15} strokeWidth={2} />
                        New investor
                    </button>
                </div>
            </header>

            {/* Filter chips */}
            <div className="pipeline-filters">
                <span className="filter-label">Status</span>
                {STATUS_FILTERS.map(s => (
                    <button
                        key={s}
                        className={`chip${statusFilter === s ? ' is-active' : ''}`}
                        onClick={() => setStatusFilter(statusFilter === s ? null : s)}
                    >
                        {s}
                    </button>
                ))}
                <span className="filter-divider" />
                <span className="filter-label">Fit</span>
                {FIT_FILTERS.map(f => (
                    <button
                        key={f}
                        className={`chip chip-fit chip-fit-${f.toLowerCase()}${fitFilter === f ? ' is-active' : ''}`}
                        onClick={() => setFitFilter(fitFilter === f ? null : f)}
                    >
                        {f}
                    </button>
                ))}
                {hasFilters && (
                    <button onClick={clearFilters} className="chip-clear">
                        Clear all
                    </button>
                )}
            </div>

            {/* Body: list + detail */}
            <div className="pipeline-body">
                <InvestorList
                    investors={investors}
                    selectedId={effectiveSelectedId}
                    onSelect={setSelectedId}
                    isLoading={isLoading}
                    error={error}
                />
                <InvestorDetail
                    investorId={effectiveSelectedId}
                    onUpdated={() => queryClient.invalidateQueries({ queryKey: ['investors'] })}
                />
            </div>

            {creating && (
                <NewInvestorModal
                    onClose={() => setCreating(false)}
                    onCreated={(investor) => {
                        queryClient.invalidateQueries({ queryKey: ['investors'] });
                        setSelectedId(investor.id);   // jump to the new record
                    }}
                />
            )}
        </div>
    );
}
